import {
  Box,
  Flex,
  SimpleGrid,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Stack,
  Text,
  Icon,
  Heading,
  Checkbox,
  Select,
  Skeleton,
  SkeletonText,
  HStack,
  VStack,
  Badge,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import {
  FiActivity,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiPrinter,
  FiDownload,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiList,
} from "react-icons/fi";
import styles from "./Home.module.css";
import HomeSortableTh from "./HomeSortableTh";

const BRAND = "#C1272D";

function StatCard({ label, value, hint, icon, accent, valueColor }) {
  return (
    <Box className={styles.statCard} borderLeftColor={accent}>
      <Flex justify="space-between" align="flex-start">
        <Box>
          <Text fontSize="sm" color="gray.500" fontWeight="medium">
            {label}
          </Text>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color={valueColor || "gray.800"}
            lineHeight="1.1"
            mt={1}
          >
            {value}
          </Text>
          <Text fontSize="xs" color="gray.400" mt={2}>
            {hint}
          </Text>
        </Box>
        <Flex
          w="44px"
          h="44px"
          align="center"
          justify="center"
          borderRadius="xl"
          bg={`${accent}15`}
        >
          <Icon as={icon} boxSize={5} color={accent} />
        </Flex>
      </Flex>
    </Box>
  );
}

export default function HomeDashboardView({
  userName,
  apiInProgressCount,
  apiDelayedCount,
  apiCompletedCount,
  filters,
  handleInputChange,
  handleApplyFilters,
  sortBy,
  handleSortChange,
  handleColumnSort,
  sortOptions,
  priorityMap,
  statusMap,
  loading,
  error,
  filteredData,
  serviceTypeMap,
  companyAddressMap,
  selectedRows,
  handleSelectAllRows,
  handleSelectRow,
  isAllSelected,
  isAnySelected,
  getPriorityColor,
  getStatusColor,
  handleOpenDetails,
  handlePrintSelected,
  handleExportToExcel,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  totalElementsFromBackend,
  totalPagesFromBackend,
}) {
  const totalOs = apiInProgressCount + apiDelayedCount + apiCompletedCount;
  const greetingName = userName?.split(" ")[0] || "bem-vindo";

  return (
    <Box className={styles.page} w="100%">
      <Box className={styles.container} w="100%">
        <Box className={styles.hero} w="100%">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
            w="100%"
          >
            <Box flex="1">
              <Badge
                colorScheme="red"
                variant="subtle"
                borderRadius="full"
                px={3}
                py={1}
                mb={3}
              >
                Painel operacional
              </Badge>
              <Heading size="lg" color="gray.800" mb={1}>
                Olá, {greetingName}
              </Heading>
              <Text color="gray.600" fontSize="md">
                Acompanhe ordens de serviço, prioridades e prazos em tempo real.
              </Text>
            </Box>
            <HStack
              spacing={3}
              bg="white"
              px={4}
              py={3}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.100"
              boxShadow="sm"
            >
              <Icon as={FiActivity} color={BRAND} boxSize={5} />
              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                  Dashboard
                </Text>
                <Text fontWeight="bold" color="gray.800">
                  Ordens de Serviço
                </Text>
              </Box>
            </HStack>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={5} mb={6}>
          <StatCard
            label="Total de OS"
            value={totalOs}
            hint="Registros no período"
            icon={FiList}
            accent="#4A5568"
          />
          <StatCard
            label="Em andamento"
            value={apiInProgressCount}
            hint="Pendentes ou em execução"
            icon={FiClock}
            accent="#3182CE"
          />
          <StatCard
            label="Atrasadas"
            value={apiDelayedCount}
            hint="Fora do prazo previsto"
            icon={FiAlertTriangle}
            accent={BRAND}
            valueColor={BRAND}
          />
          <StatCard
            label="Concluídas"
            value={apiCompletedCount}
            hint="Finalizadas com sucesso"
            icon={FiCheckCircle}
            accent="#38A169"
            valueColor="green.600"
          />
        </SimpleGrid>

        <Box className={styles.panel} mb={6} w="100%">
          <Flex align="center" gap={2} mb={4}>
            <Icon as={FiFilter} color={BRAND} />
            <Text fontWeight="semibold" color="gray.700">
              Filtros de busca
            </Text>
          </Flex>
          <Stack direction={{ base: "column", lg: "row" }} spacing={3}>
            <Input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleInputChange}
              bg="gray.50"
              borderColor="gray.200"
              flex="1"
            />
            <Input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleInputChange}
              bg="gray.50"
              borderColor="gray.200"
              flex="1"
            />
            <Select
              name="priority"
              placeholder="Prioridade"
              value={filters.priority}
              onChange={handleInputChange}
              bg="gray.50"
              borderColor="gray.200"
              flex="1"
            >
              <option value="">Todas</option>
              {Object.entries(priorityMap).map(([id, description]) => (
                <option key={id} value={id}>
                  {description}
                </option>
              ))}
            </Select>
            <Select
              name="status"
              placeholder="Situação"
              value={filters.status}
              onChange={handleInputChange}
              bg="gray.50"
              borderColor="gray.200"
              flex="1"
            >
              <option value="">Todas</option>
              {Object.entries(statusMap).map(([id, description]) => (
                <option key={id} value={id}>
                  {description}
                </option>
              ))}
            </Select>
            <Input
              name="os"
              placeholder="Número da OS"
              value={filters.os}
              onChange={handleInputChange}
              bg="gray.50"
              borderColor="gray.200"
              flex="1"
            />
            <Select
              value={sortBy}
              onChange={handleSortChange}
              bg="gray.50"
              borderColor="gray.200"
              flex="1"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button
              bg={BRAND}
              color="white"
              _hover={{ bg: "#a81f23" }}
              leftIcon={<SearchIcon />}
              onClick={handleApplyFilters}
              flexShrink={0}
              px={8}
            >
              Buscar
            </Button>
          </Stack>
        </Box>

        <Flex mb={4} justify="space-between" align="center" wrap="wrap" gap={3}>
          <Text fontSize="sm" color="gray.500">
            {isAnySelected
              ? `${selectedRows.length} OS selecionada(s)`
              : "Selecione linhas para exportar ou imprimir"}
          </Text>
          <HStack spacing={3}>
            <Button
              variant="outline"
              borderColor="gray.300"
              leftIcon={<FiPrinter />}
              onClick={handlePrintSelected}
              isDisabled={!isAnySelected}
            >
              Imprimir
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<FiDownload />}
              onClick={handleExportToExcel}
              isDisabled={!isAnySelected}
            >
              Exportar Excel
            </Button>
          </HStack>
        </Flex>

        <Box className={styles.tablePanel} w="100%">
          {loading && (
            <VStack align="stretch" spacing={4} p={6}>
              <Skeleton height="20px" />
              <SkeletonText noOfLines={8} spacing={4} />
            </VStack>
          )}

          {error && (
            <Box p={8} textAlign="center">
              <Text color="red.500" fontWeight="medium">
                {error}
              </Text>
            </Box>
          )}

          {!loading && !error && (
            <Box overflowX="auto" w="100%">
              <Table variant="simple" size="md" w="100%" minW="900px">
                <Thead>
                  <Tr>
                    <Th>
                      <Checkbox
                        isChecked={isAllSelected}
                        onChange={handleSelectAllRows}
                        isDisabled={filteredData.length === 0}
                        colorScheme="red"
                      />
                    </Th>
                    <HomeSortableTh
                      field="orderNumber"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                      defaultDirection="desc"
                    >
                      Número OS
                    </HomeSortableTh>
                    <HomeSortableTh
                      field="requesterName"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                    >
                      Solicitante
                    </HomeSortableTh>
                    <HomeSortableTh
                      field="summary"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                    >
                      Serviço
                    </HomeSortableTh>
                    <HomeSortableTh
                      field="dateExpected"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                    >
                      Agendamento
                    </HomeSortableTh>
                    <HomeSortableTh
                      field="priorityId"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                      defaultDirection="desc"
                    >
                      Prioridade
                    </HomeSortableTh>
                    <HomeSortableTh
                      field="situationId"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                    >
                      Situação
                    </HomeSortableTh>
                    <HomeSortableTh
                      field="unityName"
                      sortBy={sortBy}
                      onColumnSort={handleColumnSort}
                    >
                      Unidade
                    </HomeSortableTh>
                    <Th textAlign="right">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredData.map((item) => (
                    <Tr key={item.serviceOrderid} className={styles.tableRow}>
                      <Td>
                        <Checkbox
                          isChecked={selectedRows.includes(item.serviceOrderid)}
                          onChange={(e) =>
                            handleSelectRow(item.serviceOrderid, e)
                          }
                          colorScheme="red"
                        />
                      </Td>
                      <Td>
                        <Text fontWeight="bold" color="gray.800">
                          {item.orderNumber || "N/A"}
                        </Text>
                      </Td>
                      <Td>{item.requesterName || "N/A"}</Td>
                      <Td>
                        {serviceTypeMap[item.companyRequests?.optionsId] ||
                          item.summary ||
                          "N/A"}
                      </Td>
                      <Td>
                        {new Date(item.dateExpected).toLocaleDateString(
                          "pt-BR"
                        ) || "N/A"}
                      </Td>
                      <Td>
                        <Tag
                          size="sm"
                          borderRadius="full"
                          colorScheme={getPriorityColor(item.priorityId || "")}
                        >
                          {priorityMap[item.priorityId] || "N/A"}
                        </Tag>
                      </Td>
                      <Td>
                        <Tag
                          size="sm"
                          borderRadius="full"
                          colorScheme={getStatusColor(item.situation || "")}
                        >
                          {statusMap[item.situation] || "N/A"}
                        </Tag>
                      </Td>
                      <Td>
                        {companyAddressMap[item.companyAddressId]?.unityName ||
                          "N/A"}
                      </Td>
                      <Td textAlign="right">
                        <Button
                          size="sm"
                          variant="outline"
                          borderColor={BRAND}
                          color={BRAND}
                          _hover={{ bg: "red.50" }}
                          onClick={() => handleOpenDetails(item)}
                        >
                          Detalhes
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                  {filteredData.length === 0 && (
                    <Tr>
                      <Td colSpan={9}>
                        <Box py={12} textAlign="center">
                          <Text color="gray.500" fontSize="lg" mb={1}>
                            Nenhuma ordem encontrada
                          </Text>
                          <Text color="gray.400" fontSize="sm">
                            Ajuste os filtros ou o período e tente novamente.
                          </Text>
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>

        {!loading && !error && totalElementsFromBackend > 0 && (
          <Flex
            className={styles.pagination}
            justify="space-between"
            align="center"
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <Text fontSize="sm" color="gray.500">
              Página {currentPage + 1} de {totalPagesFromBackend} ·{" "}
              {totalElementsFromBackend} itens
            </Text>

            <HStack spacing={2}>
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                isDisabled={currentPage === 0}
                leftIcon={<FiChevronLeft />}
                variant="outline"
                size="sm"
              >
                Anterior
              </Button>

              {Array.from({ length: totalPagesFromBackend }, (_, i) => i).map(
                (pageNumber) =>
                  (pageNumber === 0 ||
                    pageNumber === totalPagesFromBackend - 1 ||
                    (pageNumber >= currentPage - 2 &&
                      pageNumber <= currentPage + 2)) && (
                    <Button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      size="sm"
                      variant={currentPage === pageNumber ? "solid" : "outline"}
                      bg={currentPage === pageNumber ? BRAND : "white"}
                      color={currentPage === pageNumber ? "white" : "gray.700"}
                      borderColor={
                        currentPage === pageNumber ? BRAND : "gray.200"
                      }
                      _hover={{
                        bg: currentPage === pageNumber ? "#a81f23" : "gray.50",
                      }}
                    >
                      {pageNumber + 1}
                    </Button>
                  )
              )}

              <Button
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(totalPagesFromBackend - 1, prev + 1)
                  )
                }
                isDisabled={
                  currentPage === totalPagesFromBackend - 1 ||
                  totalPagesFromBackend === 0
                }
                rightIcon={<FiChevronRight />}
                variant="outline"
                size="sm"
              >
                Próxima
              </Button>
            </HStack>

            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
              width="150px"
              size="sm"
              bg="white"
            >
              <option value={5}>5 por página</option>
              <option value={10}>10 por página</option>
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
            </Select>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
